// Core de portadas imprimibles compartido entre cliente final y administrador.
// Define clases de layout, marcas de corte y colofón sin acoplarse al DOM ni a librerías específicas.

// Utilidades de unidades y fechas -----------------------------

export const UnitUtils = {
  /**
   * Convierte centímetros a píxeles usando un DPI dado.
   * En el proyecto se asume 300 DPI (coherente con los helpers de Ruby).
   */
  cmToPx(cm, dpi = 300) {
    const inches = cm / 2.54
    return inches * dpi
  },
}

export const DateUtils = {
  addDays(date, days) {
    const d = new Date(date)
    d.setDate(d.getDate() + days)
    return d
  },

  formatEsMx(date) {
    return new Intl.DateTimeFormat('es-MX', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date)
  },
}

// Modelos de layout -------------------------------------------

export class PageSize {
  constructor({ widthCm, heightCm }) {
    this.widthCm = widthCm
    this.heightCm = heightCm
  }

  toPixels(dpi = 300) {
    return {
      width: UnitUtils.cmToPx(this.widthCm, dpi),
      height: UnitUtils.cmToPx(this.heightCm, dpi),
    }
  }
}

export class BleedSettings {
  constructor({ bleedCm = 1 }) {
    this.bleedCm = bleedCm
  }

  toPixels(dpi = 300) {
    const v = UnitUtils.cmToPx(this.bleedCm, dpi)
    return { top: v, right: v, bottom: v, left: v }
  }
}

export class SpineSettings {
  constructor({ widthCm }) {
    this.widthCm = widthCm
  }

  toPixels(dpi = 300) {
    return UnitUtils.cmToPx(this.widthCm, dpi)
  }
}

/**
 * Representa un elemento rectangular en el layout (portada, contraportada, lomo, marco interiores, etc.).
 * Las coordenadas y dimensiones están en la unidad de trabajo elegida (normalmente píxeles a 300 DPI).
 */
export class LayoutElement {
  constructor({ x, y, width, height, type, meta = {} }) {
    this.x = x
    this.y = y
    this.width = width
    this.height = height
    this.type = type
    this.meta = meta
  }
}

export class CoverDocumentConfig {
  constructor({ pageSize, bleedSettings, spineSettings, dpi = 300 }) {
    this.pageSize = pageSize
    this.bleedSettings = bleedSettings
    this.spineSettings = spineSettings
    this.dpi = dpi
  }

  /**
   * Devuelve el tamaño total del lienzo para una portada completa:
   * contraportada + lomo + portada + rebases.
   */
  getCanvasSize() {
    const base = this.pageSize.toPixels(this.dpi)
    const bleed = this.bleedSettings.toPixels(this.dpi)
    const spineWidth = this.spineSettings.toPixels(this.dpi)

    const width =
      base.width * 2 + // contra + portada
      spineWidth +
      bleed.left +
      bleed.right

    const height = base.height + bleed.top + bleed.bottom

    return { width, height, bleed, spineWidth, base }
  }
}

export class CoverLayoutCalculator {
  constructor(config) {
    this.config = config
  }

  /**
   * Calcula las áreas principales (canvas, contraportada, lomo, portada).
   * Las áreas de contenido (back, spine, front) están posicionadas dentro del bleed.
   */
  computeAreas() {
    const { width, height, bleed, spineWidth, base } = this.config.getCanvasSize()

    // Las áreas de contenido empiezan después del bleed izquierdo y superior
    const yStart = bleed.top
    const xStart = bleed.left

    const backX = xStart
    const spineX = backX + base.width
    const frontX = spineX + spineWidth

    return {
      canvas: new LayoutElement({ x: 0, y: 0, width, height, type: 'canvas' }),
      back: new LayoutElement({
        x: backX,
        y: yStart,
        width: base.width,
        height: base.height,
        type: 'back',
      }),
      spine: new LayoutElement({
        x: spineX,
        y: yStart,
        width: spineWidth,
        height: base.height,
        type: 'spine',
      }),
      front: new LayoutElement({
        x: frontX,
        y: yStart,
        width: base.width,
        height: base.height,
        type: 'front',
      }),
    }
  }

  /**
   * Calcula el marco donde se "encaja" la hoja interior (sin rebase) sobre la portada frontal.
   * El marco representa el área visible de la portada una vez doblados los rebases.
   */
  computeInnerFrame(paddingPx = 0) {
    const { bleed } = this.config.getCanvasSize()
    const areas = this.computeAreas()

    return new LayoutElement({
      x: areas.front.x + bleed.left + paddingPx,
      y: areas.front.y + bleed.top + paddingPx,
      width: areas.front.width - bleed.left - bleed.right - paddingPx * 2,
      height: areas.front.height - bleed.top - bleed.bottom - paddingPx * 2,
      type: 'inner-frame',
    })
  }
}

// Marcas de corte ---------------------------------------------

export class CutMark {
  constructor({ x1, y1, x2, y2 }) {
    this.x1 = x1
    this.y1 = y1
    this.x2 = x2
    this.y2 = y2
  }
}

export class PrintMarksGenerator {
  constructor(config, layoutCalculator) {
    this.config = config
    this.layoutCalculator = layoutCalculator
  }

  /**
   * Marcas de corte exteriores (esquinas del área final sin rebase).
   * Genera 4 guías formando un rectángulo alrededor de todo el contenido (contraportada + lomo + portada).
   * El rectángulo es 1cm más grande por lado que el contenido (en los bordes del canvas donde está el bleed).
   * Las guías deben estar en los bordes del canvas: -1cm izquierda, +1cm derecha, -1cm arriba, +1cm abajo del contenido.
   */
  generateOuterCutMarks() {
    const { width, height, bleed } = this.config.getCanvasSize()

    const markLength = bleed.top / 2
    const marks = []

    // Las guías deben estar en los bordes del canvas (1cm fuera del contenido)
    // El contenido sin bleed va desde (bleed.left, bleed.top) hasta (width - bleed.right, height - bleed.bottom)
    // Las guías deben estar 1cm fuera: en los bordes del canvas (0, 0) y (width, height)
    const cutXLeft = 0  // Borde izquierdo del canvas (-1cm del contenido)
    const cutXRight = width  // Borde derecho del canvas (+1cm del contenido)
    const cutYTop = 0  // Borde superior del canvas (-1cm del contenido)
    const cutYBottom = height  // Borde inferior del canvas (+1cm del contenido)

    // 1. Esquina superior izquierda (left-top)
    marks.push(
      new CutMark({ x1: cutXLeft, y1: cutYTop, x2: cutXLeft, y2: cutYTop + markLength }),
      new CutMark({ x1: cutXLeft, y1: cutYTop, x2: cutXLeft + markLength, y2: cutYTop })
    )

    // 2. Esquina superior derecha (top-right)
    marks.push(
      new CutMark({ x1: cutXRight, y1: cutYTop, x2: cutXRight, y2: cutYTop + markLength }),
      new CutMark({ x1: cutXRight, y1: cutYTop, x2: cutXRight - markLength, y2: cutYTop })
    )

    // 3. Esquina inferior izquierda (bottom-left)
    marks.push(
      new CutMark({ x1: cutXLeft, y1: cutYBottom, x2: cutXLeft, y2: cutYBottom - markLength }),
      new CutMark({ x1: cutXLeft, y1: cutYBottom, x2: cutXLeft + markLength, y2: cutYBottom })
    )

    // 4. Esquina inferior derecha (bottom-right)
    marks.push(
      new CutMark({ x1: cutXRight, y1: cutYBottom, x2: cutXRight, y2: cutYBottom - markLength }),
      new CutMark({ x1: cutXRight, y1: cutYBottom, x2: cutXRight - markLength, y2: cutYBottom })
    )

    return marks
  }

  /**
   * Marcas de corte específicas del lomo (esquinas superior/inferior del lomo).
   */
  generateSpineMarks() {
    const { spine } = this.layoutCalculator.computeAreas()
    const markLength = spine.width / 4

    const topY = spine.y
    const bottomY = spine.y + spine.height
    const leftX = spine.x
    const rightX = spine.x + spine.width

    return [
      // Superior izquierda
      new CutMark({ x1: leftX, y1: topY, x2: leftX + markLength, y2: topY }),
      new CutMark({ x1: leftX, y1: topY, x2: leftX, y2: topY + markLength }),
      // Superior derecha
      new CutMark({ x1: rightX, y1: topY, x2: rightX - markLength, y2: topY }),
      new CutMark({ x1: rightX, y1: topY, x2: rightX, y2: topY + markLength }),
      // Inferior izquierda
      new CutMark({ x1: leftX, y1: bottomY, x2: leftX + markLength, y2: bottomY }),
      new CutMark({ x1: leftX, y1: bottomY, x2: leftX, y2: bottomY - markLength }),
      // Inferior derecha
      new CutMark({ x1: rightX, y1: bottomY, x2: rightX - markLength, y2: bottomY }),
      new CutMark({ x1: rightX, y1: bottomY, x2: rightX, y2: bottomY - markLength }),
    ]
  }
}

// Colofón -----------------------------------------------------

export class ColophonTemplate {
  constructor() {
    this.baseTemplate = [
      'Esta obra se concluyó de imprimir el día {{fecha_impresion}} en los talleres gráficos de LeLab en la Ciudad de México.',
      'Para su producción se utilizó {{tipo_papel}}, un material de alta calidad que complementa la estética de las imágenes.',
      'La tipografía elegida para este álbum {{tipografias}}, aporta elegancia y legibilidad a cada página.',
    ].join('\n')
  }

  render({ fechaImpresion, tipoPapel, tipografias }) {
    return this.baseTemplate
      .replace('{{fecha_impresion}}', fechaImpresion)
      .replace('{{tipo_papel}}', tipoPapel)
      .replace('{{tipografias}}', tipografias)
  }
}

export class ColophonService {
  constructor({ template = new ColophonTemplate() } = {}) {
    this.template = template
  }

  /**
   * orderMeta:
   *  - createdAt: string | Date
   *  - tipoPapel: string
   *  - tipografias: string[] | string
   */
  generate(orderMeta) {
    if (!orderMeta) return ''

    const createdAt = orderMeta.createdAt ? new Date(orderMeta.createdAt) : new Date()
    const fechaImp = DateUtils.addDays(createdAt, 2)
    const fechaStr = DateUtils.formatEsMx(fechaImp)

    const tipoPapel = orderMeta.tipoPapel || ''
    const tipografiasArray = Array.isArray(orderMeta.tipografias)
      ? orderMeta.tipografias
      : typeof orderMeta.tipografias === 'string'
        ? orderMeta.tipografias.split(',').map((t) => t.trim()).filter(Boolean)
        : []

    const tipografias = tipografiasArray.join(', ')

    return this.template.render({
      fechaImpresion: fechaStr,
      tipoPapel,
      tipografias,
    })
  }
}

// Servicio de alto nivel para compartir layout entre flujos ----

export class CoverLayoutService {
  /**
   * Crea una configuración de documento de portada desde datos físicos.
   * - pageWidthCm / pageHeightCm: tamaño final de la portada (frente) sin rebase.
   * - spineWidthCm: ancho del lomo.
   * - bleedCm: rebase a cada lado (por defecto 1 cm).
   */
  createConfig({ pageWidthCm, pageHeightCm, spineWidthCm, bleedCm = 1, dpi = 300 }) {
    const pageSize = new PageSize({ widthCm: pageWidthCm, heightCm: pageHeightCm })
    const bleed = new BleedSettings({ bleedCm })
    const spine = new SpineSettings({ widthCm: spineWidthCm })

    return new CoverDocumentConfig({
      pageSize,
      bleedSettings: bleed,
      spineSettings: spine,
      dpi,
    })
  }

  /**
   * Genera toda la información de layout a partir de dimensiones físicas.
   * Devuelve un objeto agnóstico que puede usar:
   *  - un renderer de PDF (admin / backend)
   *  - un renderer SVG/canvas (preview en CMS o fotobook editor)
   */
  buildLayout({ pageWidthCm, pageHeightCm, spineWidthCm, bleedCm = 1, dpi = 300 }) {
    const config = this.createConfig({
      pageWidthCm,
      pageHeightCm,
      spineWidthCm,
      bleedCm,
      dpi,
    })

    const calculator = new CoverLayoutCalculator(config)
    const areas = calculator.computeAreas()
    const innerFrame = calculator.computeInnerFrame()

    const marksGenerator = new PrintMarksGenerator(config, calculator)
    const cutMarks = marksGenerator.generateOuterCutMarks()
    const spineMarks = marksGenerator.generateSpineMarks()

    return {
      config,
      areas,
      innerFrame,
      cutMarks,
      spineMarks,
    }
  }
}


